import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BaseService } from 'app/shared/services/base.service';
import { BaseResponse } from 'app/types/base-response.type';
import {
    Subsidiary,
    SubsidiaryPayload,
    SubsidiaryValidationResponse,
} from 'app/types/subsidiary.type';
import { Observable, catchError, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SubsidiaryService extends BaseService {
    constructor(
        public _http: HttpClient,
        public _router: Router,
    ) {
        super(_router);
    }

    validateCode(code: string): Observable<SubsidiaryValidationResponse> {
        return this._http
            .get<SubsidiaryValidationResponse>(
                `${this._api}/api/v1/subsidiaries/validate/${code}`,
            )
            .pipe(
                catchError((error) => {
                    this.handleError(error);
                    return throwError(() => error);
                }),
            );
    }

    getAll(): Observable<BaseResponse<Subsidiary[]>> {
        return this._http
            .get<BaseResponse<Subsidiary[]>>(
                `${this._api}/api/v1/subsidiaries`,
            )
            .pipe(
                catchError((error) => {
                    this.handleError(error);
                    return throwError(() => error);
                }),
            );
    }

    create(payload: SubsidiaryPayload): Observable<BaseResponse<Subsidiary>> {
        const body = this._mapPayloadToApi(payload);

        return this._http
            .post<BaseResponse<Subsidiary>>(
                `${this._api}/api/v1/subsidiaries`,
                body,
            )
            .pipe(
                catchError((error) => {
                    this.handleError(error);
                    return throwError(() => error);
                }),
            );
    }

    update(
        id: number,
        payload: SubsidiaryPayload,
    ): Observable<BaseResponse<Subsidiary>> {
        const body = this._mapPayloadToApi(payload);

        return this._http
            .put<BaseResponse<Subsidiary>>(
                `${this._api}/api/v1/subsidiaries/${id}`,
                body,
            )
            .pipe(
                catchError((error) => {
                    this.handleError(error);
                    return throwError(() => error);
                }),
            );
    }

    delete(id: number): Observable<void> {
        return this._http
            .delete<void>(`${this._api}/api/v1/subsidiaries/${id}`)
            .pipe(
                catchError((error) => {
                    this.handleError(error);
                    return throwError(() => error);
                }),
            );
    }

    /**
     * Mapea el payload del formulario (camelCase) al formato
     * esperado por la API (snake_case) omitiendo valores vacíos.
     */
    private _mapPayloadToApi(payload: SubsidiaryPayload): Record<string, unknown> {
        const apiPayload: Record<string, unknown> = {
            fantasy_name: payload.fantasyName,
            code: payload.code,
            phone_platform: payload.phonePlatform,
            phone_id: payload.phoneId,
            connected_code: payload.connectedCode,
            domino_code: payload.dominoCode,
            precooked_start_time: payload.precookedStartTime,
            automatic_confirm: payload.automaticConfirm,
            revision_enabled: payload.revisionEnabled,
            scm_subsidiary_id: payload.scmSubsidiaryId,
        };

        return Object.entries(apiPayload).reduce(
            (acc, [key, value]) => {
                if (
                    value === undefined ||
                    (typeof value === 'string' && value.trim() === '')
                ) {
                    return acc;
                }

                acc[key] = value;
                return acc;
            },
            {} as Record<string, unknown>,
        );
    }
}
